import clsx from 'clsx';
import {
  BarChart3,
  Home,
  LogIn,
  LogOut,
  PlusSquare,
  Settings,
  Shield,
  Swords,
  User,
  Users,
} from 'lucide-react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

const nav = [
  { to: '/', label: 'Start', icon: Home, end: true },
  { to: '/series', label: 'Serien', icon: Swords, end: false },
  { to: '/players', label: 'Spieler', icon: Users },
  { to: '/stats', label: 'Stats', icon: BarChart3 },
];

export function Layout() {
  const { user, can, logout, status } = useAuth();

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-b border-ink-700/70 bg-ink-950/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-4">
          <Link
            to="/"
            className="flex items-center gap-2 font-display text-xl font-bold tracking-wider uppercase"
          >
            <img src="/favicon.svg" alt="" className="size-7" />
            <span className="hidden sm:inline">
              Valo <span className="text-accent">Tracker</span>
            </span>
          </Link>
          <nav className="hidden flex-1 items-center gap-1 md:flex">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'px-3 py-2 font-display text-sm font-bold tracking-wider uppercase transition',
                    isActive ? 'text-accent' : 'text-ink-300 hover:text-bone',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            {can('admin') && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  clsx(
                    'px-3 py-2 font-display text-sm font-bold tracking-wider uppercase',
                    isActive ? 'text-accent' : 'text-ink-300 hover:text-bone',
                  )
                }
              >
                Admin
              </NavLink>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            {status === 'authenticated' && user ? (
              <>
                {can('admin') && (
                  <Link
                    to="/admin"
                    className="p-2 text-ink-300 hover:text-bone md:hidden"
                    aria-label="Admin"
                  >
                    <Shield className="size-5" />
                  </Link>
                )}
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-2 py-1 text-sm text-ink-300 hover:text-bone"
                >
                  <User className="size-4" />
                  <span className="max-w-28 truncate">{user.username}</span>
                </Link>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="p-2 text-ink-400 hover:text-bone"
                  aria-label="Abmelden"
                  title="Abmelden"
                >
                  <LogOut className="size-4" />
                </button>
              </>
            ) : status === 'guest' ? (
              <Link
                to="/login"
                className="flex items-center gap-1.5 px-2 py-1 font-display text-sm font-bold tracking-wider text-ink-300 uppercase hover:text-bone"
              >
                <LogIn className="size-4" /> Login
              </Link>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pt-6 pb-24 md:pb-10">
        <Outlet />
      </main>

      <footer className="border-t border-ink-800 px-4 pt-6 pb-24 text-center text-xs leading-relaxed text-ink-500 md:pb-6">
        <p className="mx-auto max-w-3xl">
          Valo Match Tracker ist ein privates Fan-Projekt und steht in keiner Verbindung zu Riot
          Games. Es wird weder von Riot Games unterstützt noch gibt es die Ansichten oder Meinungen
          von Riot Games oder von Personen wieder, die offiziell an der Produktion oder Verwaltung
          von Riot-Games-Inhalten beteiligt sind. Riot Games, VALORANT und alle zugehörigen Inhalte
          sind Marken oder eingetragene Marken von Riot Games, Inc.
        </p>
      </footer>

      {/* Mobile bottom navigation */}
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-ink-700 bg-ink-950/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        {[
          ...nav,
          can('organizer')
            ? { to: '/series/new', label: 'Neu', icon: PlusSquare, end: true }
            : {
                to: status === 'authenticated' ? '/profile' : '/login',
                label: status === 'authenticated' ? 'Profil' : 'Login',
                icon: status === 'authenticated' ? Settings : LogIn,
                end: true,
              },
        ].map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center gap-0.5 py-2 text-[11px] font-semibold tracking-wide uppercase',
                isActive ? 'text-accent' : 'text-ink-400',
              )
            }
          >
            <item.icon className="size-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
